import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { PartnerPicker } from "../components/PartnerPicker";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Plug, Loader2, Zap, Gauge, Clock, Car, Sun, ExternalLink, ShoppingCart, X, CheckCircle2, AlertTriangle, ArrowRight, Fuel, Download } from "lucide-react";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";

const postToParent = (msg) => { try { if (window.parent && window.parent !== window) window.parent.postMessage(msg, "*"); } catch (_) {} };
const fmt = (n) => Math.round(Number(n)).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
const eur = (n) => (n == null ? "—" : fmt(n) + " €");
const num = (n) => (n == null ? "—" : fmt(n));

function ReteColonninaHeader({ brand }) {
  const inst = brand.installer || {};
  const partner = brand.brand_mode === "partner";
  const Torri = () => (
    <div className="flex items-center gap-2">
      <img src="/torri-icon.png" alt="TORRI.SOLAR" className="h-[41px] w-[41px] rounded-full object-contain" />
      <div className="leading-none">
        <div className="font-heading font-extrabold text-[19px] tracking-wide">TORRI<span style={{ color: "#FDD003" }}>.SOLAR</span></div>
        <div className="text-[11px] tracking-[0.16em] uppercase text-muted-foreground mt-0.5">Div. Chiavi in mano</div>
      </div>
    </div>
  );
  const Anag = ({ align }) => (
    <div className={`leading-tight min-w-0 ${align === "right" ? "text-right" : ""}`}>
      <div className="font-heading font-extrabold text-sm break-words">{inst.ragione_sociale}</div>
      {inst.indirizzo && <div className="text-[10px] text-muted-foreground">{inst.indirizzo}</div>}
      {[inst.citta, inst.provincia].filter(Boolean).length > 0 && <div className="text-[10px] text-muted-foreground">{[inst.citta, inst.provincia].filter(Boolean).join(" · ")}</div>}
      {inst.p_iva && <div className="text-[10px] text-muted-foreground">P.IVA {inst.p_iva}</div>}
      {inst.telefono && <div className="text-[10px] text-muted-foreground">Tel. {inst.telefono}</div>}
      {inst.email && <div className="text-[10px] text-muted-foreground break-all">{inst.email}</div>}
    </div>
  );
  return (
    <div className="flex items-start justify-between gap-4 mb-6 border-b border-border pb-4" data-testid="colonnina-cobrand-header">
      {partner ? (
        <div className="flex items-center gap-2.5">
          {inst.logo_url ? <img src={inst.logo_url} alt={inst.ragione_sociale} className="h-[52px] w-[52px] rounded-lg object-contain bg-white border border-border" onError={(e) => { e.currentTarget.style.display = "none"; }} /> : <div className="h-[52px] w-[52px] rounded-lg bg-primary/10 text-primary flex items-center justify-center font-heading font-extrabold">{(inst.ragione_sociale || "?").charAt(0).toUpperCase()}</div>}
          <Anag />
        </div>
      ) : <Torri />}
      <div className="flex flex-col items-end gap-1 shrink-0">
        {partner ? <Torri /> : (<>
          <span className="text-[10px] text-muted-foreground text-right" data-testid="colonnina-featuring">In collaborazione con l'installatore</span>
          <Anag align="right" />
        </>)}
        {partner && brand.partner_note && <span className="text-[9px] font-semibold uppercase tracking-wide text-primary bg-primary/10 rounded-full px-2 py-0.5">{brand.partner_note}</span>}
      </div>
    </div>
  );
}

export default function FreeColonnina({ printMode = false }) {
  const navigate = useNavigate();
  const { token: routeToken } = useParams();
  const qs = new URLSearchParams(window.location.search);
  const reteMode = qs.get("rete") === "1" || printMode;
  const isPdfExport = printMode || qs.get("pdf") === "1";
  const [step, setStep] = useState(0);
  const [models, setModels] = useState([]);
  const [selfToken, setSelfToken] = useState(routeToken || null);
  const [busy, setBusy] = useState(false);
  const [partner, setPartner] = useState({ via_partner: false, partner_id: "", partner_name: "", partner_logo_url: "" });
  const [reco, setReco] = useState(null);
  const [sent, setSent] = useState(false);
  const [reteBrand, setReteBrand] = useState(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [form, setForm] = useState({ model_label: "", consumo_kwh_100: "", km_value: "", km_unit: "anno", contatore: "unknown", potenza_kw: "", parcheggio: "notte" });
  const [contact, setContact] = useState({ nome: "", cognome: "", email: "", phone: "" });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setC = (k, v) => setContact((c) => ({ ...c, [k]: v }));

  useEffect(() => {
    if (!printMode) api.get("/public/ev-models").then((r) => setModels(r.data.models || [])).catch(() => {});
    const s = routeToken || qs.get("self");
    if (s) {
      setSelfToken(s);
      api.get(`/public/self/${s}/state`).then(({ data }) => {
        if (data?.rete) setReteBrand(data.rete);
        if ((printMode || (qs.get("rete") === "1")) && data?.colonnina) { setReco(data.colonnina); setStep(1); }
      }).catch(() => {});
    }
  }, []);

  const downloadPdf = async () => {
    if (!selfToken) return;
    setPdfBusy(true);
    setPdfUrl(null);
    try {
      const { data: job } = await api.post(`/public/print-pdf-job/${selfToken}?kind=colonnina`);
      let tries = 0, status = "pending";
      while (status === "pending" && tries < 90) {
        await new Promise((r) => setTimeout(r, 2000)); tries++;
        const { data: st } = await api.get(`/public/print-pdf-status/${job.job_id}`);
        status = st.status;
        if (status === "error") throw new Error(st.error || "Errore");
      }
      if (status !== "done") throw new Error("Timeout");
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/public/print-pdf-result/${job.job_id}`;
      setPdfUrl(url);
      toast.success('PDF pronto! Clicca "Apri il PDF".');
    } catch (e) {
      toast.error("Generazione PDF non riuscita, riprova");
    } finally { setPdfBusy(false); }
  };

  const kmYear = useMemo(() => {
    const v = Number(form.km_value);
    if (!v) return null;
    return form.km_unit === "giorno" ? Math.round(v * 365) : Math.round(v);
  }, [form.km_value, form.km_unit]);

  const onModel = (label) => {
    const m = models.find((x) => `${x.brand} ${x.model}` === label);
    set("model_label", label);
    if (m) set("consumo_kwh_100", String(m.kwh_100km));
  };

  const calc = async () => {
    setBusy(true);
    try {
      let tok = selfToken;
      if (!tok) {
        const { data } = await api.post("/public/free/start", { tool: "colonnina", ...partner });
        tok = data.self_token; setSelfToken(tok);
      }
      const payload = {
        contatore: form.contatore, potenza_kw: form.potenza_kw ? Number(form.potenza_kw) : null,
        model_label: form.model_label || null, consumo_kwh_100: form.consumo_kwh_100 ? Number(form.consumo_kwh_100) : null,
        km_year: kmYear, parcheggio_ore: form.parcheggio === "poche" ? 3 : 8,
      };
      const { data } = await api.post(`/public/free/colonnina/${tok}/config`, payload);
      setReco(data); setStep(1);
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore nel calcolo"); }
    finally { setBusy(false); }
  };

  const register = async () => {
    if (!contact.nome.trim() || !contact.cognome.trim()) return toast.error("Inserisci nome e cognome");
    setBusy(true);
    try {
      await api.post(`/public/free/colonnina/${selfToken}/register`, { ...contact, origin_url: window.location.origin });
      setSent(true);
      toast.success("Riepilogo inviato via email");
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore invio"); }
    finally { setBusy(false); }
  };

  const openUrl = (url) => { postToParent({ type: "torri-colonnina", action: "navigate", url }); window.open(url, "_blank", "noopener"); };
  const closeWidget = () => { postToParent({ type: "torri-colonnina", action: "close" }); };

  return (
    <div className={`min-h-screen bg-background text-foreground px-4 flex justify-center${isPdfExport ? " print-surface pdf-light py-5" : " py-8"}`} data-testid="free-colonnina">
      <div className="w-full max-w-xl">
        {reteBrand?.installer?.ragione_sociale ? (
          <ReteColonninaHeader brand={reteBrand} />
        ) : (
        <div className="flex items-center gap-2 mb-6">
          <div className="h-9 w-9 rounded-full bg-primary/15 flex items-center justify-center"><Plug className="h-4 w-4 text-primary" /></div>
          <div>
            <div className="font-heading font-extrabold tracking-wide text-sm">TORRI<span className="text-primary">.SOLAR</span></div>
            <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Configuratore Colonnina</div>
          </div>
        </div>
        )}

        {step === 0 && (
          <Card className="p-6 rounded-lg border-border space-y-5" data-testid="colonnina-step-form">
            <div>
              <h1 className="text-2xl font-heading font-bold mb-1">Quale wallbox fa per te?</h1>
              <p className="text-sm text-muted-foreground">Rispondi a poche domande: ti diciamo se ti serve la <b>7,4</b>, la <b>11</b> o la <b>22 kW</b> e quanto velocemente ricarichi.</p>
            </div>

            {!reteMode && <div className="pb-4 border-b border-border"><PartnerPicker value={partner} onChange={setPartner} /></div>}

            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground flex items-center gap-1.5"><Car className="h-3.5 w-3.5" /> La tua auto elettrica</Label>
              <Select value={form.model_label} onValueChange={onModel}>
                <SelectTrigger data-testid="colonnina-model"><SelectValue placeholder="Seleziona il modello (opzionale)" /></SelectTrigger>
                <SelectContent>
                  {models.map((m) => { const l = `${m.brand} ${m.model}`; return <SelectItem key={l} value={l} data-testid={`car-${l}`}>{l}</SelectItem>; })}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">Km percorsi</Label>
                <Input type="number" min="0" value={form.km_value} onChange={(e) => set("km_value", e.target.value)} placeholder="es. 15000" data-testid="colonnina-km" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">Periodo</Label>
                <Select value={form.km_unit} onValueChange={(v) => set("km_unit", v)}>
                  <SelectTrigger data-testid="colonnina-km-unit"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="anno">all'anno</SelectItem>
                    <SelectItem value="giorno">al giorno</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">Contatore</Label>
                <Select value={form.contatore} onValueChange={(v) => set("contatore", v)}>
                  <SelectTrigger data-testid="colonnina-contatore"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monofase" data-testid="cnt-monofase">Monofase</SelectItem>
                    <SelectItem value="trifase" data-testid="cnt-trifase">Trifase</SelectItem>
                    <SelectItem value="unknown" data-testid="cnt-unknown">Non lo so</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">Potenza contatore (kW)</Label>
                <Input type="number" min="0" step="0.5" value={form.potenza_kw} onChange={(e) => set("potenza_kw", e.target.value)} placeholder="es. 6" data-testid="colonnina-potenza" />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> Quando parcheggi per ricaricare</Label>
              <Select value={form.parcheggio} onValueChange={(v) => set("parcheggio", v)}>
                <SelectTrigger data-testid="colonnina-parcheggio"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="notte">Tutta la notte / molte ore</SelectItem>
                  <SelectItem value="poche">Poche ore</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Button onClick={calc} disabled={busy} className="w-full font-semibold" data-testid="colonnina-calc-btn">
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Zap className="h-4 w-4 mr-2" />} Scopri la wallbox consigliata
            </Button>
          </Card>
        )}

        {step === 1 && reco && (
          <div className={isPdfExport ? "space-y-3" : "space-y-4"} data-testid="colonnina-result">
            <Card className="p-6 rounded-lg border-primary/40 bg-primary/5 text-center">
              <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Wallbox consigliata</div>
              <div className="text-3xl font-heading font-extrabold text-primary" data-testid="colonnina-reco-power">{reco.potenza_label}</div>
              <div className="grid grid-cols-2 gap-3 mt-5">
                <div className="rounded border border-border bg-background p-3"><div className="text-[10px] uppercase tracking-wide text-muted-foreground flex items-center justify-center gap-1"><Gauge className="h-3 w-3" /> Capacità di ricarica oraria (km/h)</div><div className="text-lg font-bold" data-testid="colonnina-kmh">{reco.km_orari} km/h</div></div>
                <div className="rounded border border-border bg-background p-3"><div className="text-[10px] uppercase tracking-wide text-muted-foreground flex items-center justify-center gap-1"><Car className="h-3 w-3" /> Media km/giorno</div><div className="text-lg font-bold" data-testid="colonnina-km-giorno">{num(reco.km_giorno)} km</div></div>
                <div className="rounded border border-border bg-background p-3"><div className="text-[10px] uppercase tracking-wide text-muted-foreground flex items-center justify-center gap-1"><Clock className="h-3 w-3" /> Ricarica al giorno</div><div className="text-lg font-bold" data-testid="colonnina-ore-giorno">{reco.ore_ricarica_giornaliera} h</div></div>
                <div className="rounded border border-border bg-background p-3"><div className="text-[10px] uppercase tracking-wide text-muted-foreground flex items-center justify-center gap-1"><Clock className="h-3 w-3" /> Ricarica a settimana</div><div className="text-lg font-bold" data-testid="colonnina-ore-settimana">{reco.ore_ricarica_settimanale} h</div></div>
              </div>
            </Card>

            <Card className="p-5 rounded-lg border-border space-y-2" data-testid="colonnina-notes">
              {(reco.note || []).map((n, i) => (
                <div key={i} className="flex items-start gap-2 text-sm text-muted-foreground"><CheckCircle2 className="h-4 w-4 text-primary shrink-0 mt-0.5" /><span>{n}</span></div>
              ))}
            </Card>

            {reco.risparmio_vs_gasolio_anno > 0 && (
              <Card className="p-5 rounded-lg border-emerald-400/50 bg-emerald-50/60 dark:bg-emerald-500/10" data-testid="colonnina-diesel">
                <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-emerald-700 dark:text-emerald-400 mb-1"><Fuel className="h-4 w-4" /> Rispetto al gasolio</div>
                <div className="text-sm">Passando da un'auto a gasolio all'elettrico risparmi circa <b className="text-emerald-700 dark:text-emerald-400" data-testid="colonnina-diesel-value">{eur(reco.risparmio_vs_gasolio_anno)}/anno</b><span className="text-muted-foreground"> ricaricando a casa, senza fotovoltaico.</span></div>
                <div className="text-[11px] text-emerald-800 dark:text-emerald-200/90 mt-1.5">Gasolio ~{eur(reco.costo_gasolio_anno)}/anno ({num(reco.consumo_gasolio_l100)} L/100km a {String(reco.prezzo_gasolio_l).replace(".", ",")} €/L) · Ricarica a casa ~{eur(reco.costo_ev_rete_anno)}/anno · {num(reco.fabbisogno_kwh_anno)} kWh/anno · Fonte prezzo gasolio: MIMIT</div>
              </Card>
            )}

            {reco.risparmio_fv_anno > 0 && (
              <Card className="p-5 rounded-lg border-amber-400/50 bg-amber-50/60 dark:bg-amber-500/10" data-testid="colonnina-savings">
                <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-amber-700 dark:text-amber-400 mb-1"><Sun className="h-4 w-4" /> Con il fotovoltaico</div>
                <div className="text-sm">Installando un impianto <b>fotovoltaico</b> ricarichi con il sole e risparmi circa <b className="text-amber-700 dark:text-amber-400" data-testid="colonnina-savings-value">{eur(reco.risparmio_fv_anno)}/anno</b> sul costo di ricarica.</div>
                <div className="text-[11px] text-amber-800 dark:text-amber-200/90 mt-1.5">Stima pari a circa l'{Math.round(reco.fv_saving_pct * 100)}% del costo di ricarica da rete.</div>
                {reco.risparmio_totale_anno > 0 && (
                  <div className="text-sm mt-2 border-t border-amber-400/30 pt-2">Totale rispetto a un'auto a gasolio, ricaricando col sole: <b className="text-amber-700 dark:text-amber-400" data-testid="colonnina-total-value">{eur(reco.risparmio_totale_anno)}/anno</b>.</div>
                )}
              </Card>
            )}

            {!reteMode && (reco.buy_url || reco.product_url) && (
              <Card className="p-5 rounded-lg border-border flex items-center gap-4" data-testid="colonnina-qr-card">
                <span className="rounded-md bg-white p-2 border border-border shrink-0">
                  <QRCodeSVG value={reco.buy_url || reco.product_url} size={84} level="M" bgColor="#ffffff" fgColor="#1f1f1f" />
                </span>
                <div className="min-w-0">
                  <div className="font-heading font-semibold text-sm">Acquista la wallbox consigliata</div>
                  <p className="text-xs text-muted-foreground">Inquadra il QR code con il telefono per aprire la pagina del prodotto{reco.potenza_label ? ` (${reco.potenza_label})` : ""} e completare l'acquisto.</p>
                </div>
              </Card>
            )}

            {!isPdfExport && (
            <button onClick={() => navigate(selfToken ? `/free/bolletta?self=${selfToken}` : "/free/bolletta")} className="w-full flex items-center justify-center gap-2 rounded-lg border border-primary/40 bg-primary/10 hover:bg-primary/20 transition-colors py-3 text-sm font-semibold text-primary" data-testid="colonnina-pv-cta">
              <Sun className="h-4 w-4" /> Calcola il tuo impianto fotovoltaico <ArrowRight className="h-4 w-4" />
            </button>
            )}

            {!isPdfExport && (
            <div className="flex flex-wrap gap-2">
              <Button onClick={() => openUrl(reco.product_url)} variant="outline" className="flex-1 font-semibold" data-testid="colonnina-see-product"><ExternalLink className="h-4 w-4 mr-1.5" /> Vedi prodotto</Button>
              {!reteMode && <Button onClick={() => openUrl(reco.buy_url)} className="flex-1 font-semibold" data-testid="colonnina-buy"><ShoppingCart className="h-4 w-4 mr-1.5" /> Acquista</Button>}
              {!reteMode && <Button onClick={closeWidget} variant="ghost" className="text-muted-foreground" data-testid="colonnina-close"><X className="h-4 w-4 mr-1.5" /> Chiudi</Button>}
            </div>
            )}

            {reteMode && !isPdfExport && (
              <Card className="p-5 rounded-lg border-primary/40 bg-primary/5 flex flex-col sm:flex-row items-center justify-between gap-4" data-testid="colonnina-pdf-card">
                <div>
                  <div className="font-heading font-semibold">Relazione colonnina</div>
                  <p className="text-sm text-muted-foreground">Scarica il PDF co-brandizzato con la wallbox consigliata e il risparmio stimato.</p>
                </div>
                <div className="flex flex-col items-stretch gap-2 shrink-0">
                  <Button onClick={downloadPdf} disabled={pdfBusy} className="font-semibold whitespace-nowrap" data-testid="colonnina-download-pdf">
                    {pdfBusy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null} {pdfBusy ? "Generazione…" : (pdfUrl ? "Rigenera PDF" : "Scarica PDF")}
                  </Button>
                  {pdfUrl && !pdfBusy && (
                    <a href={pdfUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-1.5 rounded-md bg-primary text-primary-foreground font-semibold text-sm py-2 px-3 whitespace-nowrap hover:opacity-90" data-testid="colonnina-open-pdf">
                      <Download className="h-4 w-4" /> Apri il PDF
                    </a>
                  )}
                  {pdfBusy && (
                    <p className="text-xs text-muted-foreground text-center max-w-[9rem] leading-snug" data-testid="colonnina-pdf-generating">
                      Sto generando il PDF… resta su questa pagina.
                    </p>
                  )}
                </div>
              </Card>
            )}

            {!reteMode && !isPdfExport && (!sent ? (
              <Card className="p-5 rounded-lg border-border" data-testid="colonnina-contact">
                <div className="font-heading font-semibold mb-1">Ricevi il riepilogo via email</div>
                <p className="text-xs text-muted-foreground mb-3">Ti inviamo il consiglio e un nostro consulente resta a disposizione (facoltativo).</p>
                <div className="grid grid-cols-2 gap-3">
                  <Input value={contact.nome} onChange={(e) => setC("nome", e.target.value)} placeholder="Nome" data-testid="colonnina-nome" />
                  <Input value={contact.cognome} onChange={(e) => setC("cognome", e.target.value)} placeholder="Cognome" data-testid="colonnina-cognome" />
                  <Input value={contact.email} onChange={(e) => setC("email", e.target.value)} placeholder="Email" data-testid="colonnina-email" />
                  <Input value={contact.phone} onChange={(e) => setC("phone", e.target.value)} placeholder="Telefono" data-testid="colonnina-phone" />
                </div>
                <Button onClick={register} disabled={busy} className="w-full mt-3 font-semibold" data-testid="colonnina-register-btn">
                  {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null} Invia il riepilogo
                </Button>
              </Card>
            ) : (
              <Card className="p-5 rounded-lg border-success/40 bg-success/10 flex items-center gap-2" data-testid="colonnina-sent"><CheckCircle2 className="h-5 w-5 text-success" /><span className="text-sm">Ti abbiamo inviato il riepilogo via email. A presto!</span></Card>
            ))}

            {!isPdfExport && (
            <button onClick={() => { setStep(0); setReco(null); }} className="text-xs text-muted-foreground hover:text-foreground w-full text-center" data-testid="colonnina-back">← Rifai il calcolo</button>
            )}

            {!isPdfExport && (
            <div className="pt-5 mt-2 border-t border-border text-[10px] leading-relaxed text-muted-foreground" data-testid="colonnina-legal-footer">
              <div className="font-semibold text-foreground">TORRI.SOLAR è un marchio di Fier S.p.A.</div>
              <div>Fier S.p.A. a socio unico · Via Cremona, 123 - 25025 Manerbio (BS) · P.IVA/C.F. 03720770985 — Cap. soc. 500.000,00 € i.v.</div>
              <div>Registro AEE n. IT16120000009638 · Registro Pile e Accumulatori n. IT23100P00009100</div>
            </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
